/**
 * modes/walker-controller.js
 * Quản lý tập trung bộ điều khiển chuyển động và camera cho chế độ NPC Đi Dạo (Walker Mode).
 * Đóng gói toàn bộ logic tính toán vận tốc, bước thời gian và điều phối camera responsive.
 */

import { samplePath } from './walker-paths.js';

export const WALKER_SPEED_CONFIG = {
  BASE_WALK_RATE: 5.5,
  BASE_RUN_RATE: 12.5
};

export const WALKER_CAMERA_CONFIG = {
  DESKTOP_POS: { x: 0, y: 2.5, z: 6.5 },
  COMPACT_POS: { x: 0, y: 2.6, z: 8.6 },
  TARGET: { x: 0, y: 1.0, z: 0 }
};

/**
 * Xác định xem khung nhìn hiện tại có thuộc loại bố cục compact (mobile đứng) hay không.
 * @param {number} width
 * @param {number} height
 * @returns {boolean}
 */
export function isWalkerCompactLayout(
  width = typeof window !== 'undefined' ? window.innerWidth : 1440,
  height = typeof window !== 'undefined' ? window.innerHeight : 900
) {
  return width < 900 || width / height < 1.0;
}

/**
 * Đặt lại vị trí và góc nhìn camera walker theo cấu hình chuẩn của layout hiện tại.
 * @param {object} state - App state chứa camera và controls
 */
export function updateWalkerCamera(state) {
  if (!state || !state.camera || !state.controls) return;
  const compact = isWalkerCompactLayout();
  state._lastWalkerCompact = compact;

  const pos = compact ? WALKER_CAMERA_CONFIG.COMPACT_POS : WALKER_CAMERA_CONFIG.DESKTOP_POS;
  state.camera.position.set(pos.x, pos.y, pos.z);
  state.camera.lookAt(WALKER_CAMERA_CONFIG.TARGET.x, WALKER_CAMERA_CONFIG.TARGET.y, WALKER_CAMERA_CONFIG.TARGET.z);
  state.controls.target.set(
    WALKER_CAMERA_CONFIG.TARGET.x,
    WALKER_CAMERA_CONFIG.TARGET.y,
    WALKER_CAMERA_CONFIG.TARGET.z
  );
  state.controls.update();
}

/**
 * Đồng bộ camera walker khi resize cửa sổ: CHỈ đặt lại khi layout thay đổi loại (compact <-> desktop).
 * Giữ nguyên góc nhìn/zoom người dùng đã tùy chỉnh nếu resize trong cùng một loại layout.
 * @param {object} state - App state
 */
export function syncWalkerCameraOnResize(state) {
  if (!state || !state.camera || state.currentMode !== 'walker') return;
  const compact = isWalkerCompactLayout();
  if (state._lastWalkerCompact === undefined || state._lastWalkerCompact !== compact) {
    updateWalkerCamera(state);
  }
}

/**
 * Tính tỉ lệ tốc độ dựa theo action hiện tại ('walk' hoặc 'run').
 * @param {object} av - Avatar instance
 * @returns {number|null} Tỉ lệ tốc độ hoặc null nếu action không thuộc diện di chuyển
 */
function getWalkerSpeedRatio(av) {
  const action = av.animator?.currentAction || av.animator?.targetAction || 'walk';
  if (action !== 'walk' && action !== 'run') return null;
  if (action === 'run') {
    const walkSpeed = av.animator?.walkSpeed || WALKER_SPEED_CONFIG.BASE_WALK_RATE;
    return WALKER_SPEED_CONFIG.BASE_RUN_RATE / walkSpeed;
  }
  return 1.0;
}

/**
 * Cập nhật chuyển động từng bước (delta time) cho NPC Walker Mode trong vòng lặp loop.
 * @param {object} av - Avatar instance
 * @param {number} delta - Khoảng thời gian giữa 2 frame (giây)
 */
export function advanceWalker(av, delta) {
  if (!av || !av.walkerController) return;
  const speedRatio = getWalkerSpeedRatio(av);
  if (speedRatio === null) return; // Đứng yên tại chỗ nếu chọn action khác (sit, wave...)

  av.walkerController.time += delta * speedRatio;
  const compact = isWalkerCompactLayout();
  const pose = samplePath(av.walkerController.pathId, av.walkerController.time, compact);

  av.root.position.x = pose.x;
  av.root.position.z = pose.z;
  av.root.rotation.y = pose.yaw;
}

/**
 * Đặt thời gian và cập nhật tư thế xác định cho NPC Walker Mode (phục vụ deterministic hooks / pauseAvatarsAt).
 * @param {object} av - Avatar instance
 * @param {number} t - Mốc thời gian (giây)
 */
export function setWalkerTime(av, t) {
  if (!av || !av.walkerController) return;
  av.walkerController.time = t;
  const speedRatio = getWalkerSpeedRatio(av);
  if (speedRatio === null) return;

  const compact = isWalkerCompactLayout();
  const pose = samplePath(av.walkerController.pathId, t * speedRatio, compact);

  av.root.position.x = pose.x;
  av.root.position.z = pose.z;
  av.root.rotation.y = pose.yaw;
}
